import { useQuery } from '@apollo/client';
import {
    InfoBox,
    TreeSelect,
    type TreeNode,
} from '@ics-portal/guardian';
import { ModalDivided } from '@components/_atoms/ModalDivided/ModalDivided';
import { type ReactNode, useMemo } from 'react';
import {
    type UseFormClearErrors,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { GET_COUNTRIES_TREE } from '@api/queries/countries';
import { Section } from '@components/shared/Section';
import { flatMapEdges } from '@utils/flatMapEdges';
import { type ProjectFormAttributes } from '../../ProjectForm';

interface CountryTreeNode {
    id: string;
    name: string;
    parent?: { id: string } | null;
    countryStandards?: { description: string }[] | null;
}

type DeliveryCountryKeys = ProjectFormAttributes['deliveryCountryKeys'];

interface CountryLookupEntry {
    name: string;
    countryStandards: { description: string }[];
}

type CountryLookup = Map<string, CountryLookupEntry>;

function buildCountryLookup(countries: CountryTreeNode[]): CountryLookup {
    return new Map(
        countries.map((country) => [
            country.id,
            {
                name: country.name,
                countryStandards: country.countryStandards ?? [],
            },
        ]),
    );
}

function getTreeNodeLabel(node: TreeNode): string {
    return node.label ? String(node.label) : String(node.key);
}

function sortTreeNodes(nodes: TreeNode[]): TreeNode[] {
    return [...nodes]
        .sort((left, right) =>
            getTreeNodeLabel(left).localeCompare(getTreeNodeLabel(right)),
        )
        .map((node) => ({
            ...node,
            children: sortTreeNodes(node.children ?? []),
        }));
}

function mapCountriesToTreeNodes(countries: CountryTreeNode[]): TreeNode[] {
    const nodeByCountryId = new Map<string, TreeNode>();

    for (const country of countries) {
        nodeByCountryId.set(country.id, {
            key: country.id,
            label: country.name,
            children: [],
        });
    }

    const rootNodes: TreeNode[] = [];

    for (const country of countries) {
        const node = nodeByCountryId.get(country.id);
        if (!node) {
            continue;
        }

        const parentId = country.parent?.id;
        const parentNode = parentId ? nodeByCountryId.get(parentId) : undefined;

        if (parentNode) {
            const parentChildren = parentNode.children ?? [];
            parentChildren.push(node);
            parentNode.children = parentChildren;
            continue;
        }

        rootNodes.push(node);
    }

    return sortTreeNodes(rootNodes);
}

function getMarketAccessTitle(label: string): string {
    return label === 'EU Countries'
        ? 'European Union Market Access'
        : `${label} Market Access`;
}

interface MarketAccessEntryProps {
    title: string;
    description: string;
}

function MarketAccessEntry({
    title,
    description,
}: MarketAccessEntryProps): ReactNode {
    return (
        <div className="flex flex-column gap-2">
            <strong>{title}</strong>
            <p className="p-0 m-0">{description}</p>
        </div>
    );
}

interface DeliveryCountrySelectorProps {
    clearErrors: UseFormClearErrors<ProjectFormAttributes>;
    options: TreeNode[];
    loading: boolean;
}

function DeliveryCountrySelector({
    clearErrors,
    options,
    loading,
}: DeliveryCountrySelectorProps): ReactNode {
    return (
        <div className="flex flex-column gap-2" style={{ flex: 525 }}>
            <label className="font-bold" htmlFor="delivery-country-keys">
                Select the country where the project will be delivered:
            </label>
            <TreeSelect
                name="deliveryCountryKeys"
                inputId="delivery-country-keys"
                options={options}
                selectionMode="checkbox"
                checkboxSelectionBehavior="ancestorsOnly"
                display="chip"
                filter
                disabled={loading}
                metaKeySelection={false}
                placeholder={
                    loading ? 'Loading countries...' : 'Select the country'
                }
                onChange={() => {
                    clearErrors('deliveryCountryKeys');
                }}
            />
        </div>
    );
}

interface MarketAccessPanelProps {
    deliveryCountryKeys: DeliveryCountryKeys | undefined;
    countryLookup: CountryLookup;
}

function MarketAccessPanel({
    deliveryCountryKeys,
    countryLookup,
}: MarketAccessPanelProps): ReactNode {
    const selectedKeys = Object.keys(deliveryCountryKeys ?? {});

    const entries = useMemo(() => {
        const results: MarketAccessEntryProps[] = [];
        const seenDescriptions = new Set<string>();

        for (const key of selectedKeys) {
            const country = countryLookup.get(key);
            if (!country) continue;

            for (const standard of country.countryStandards) {
                if (seenDescriptions.has(standard.description)) continue;
                seenDescriptions.add(standard.description);
                results.push({
                    title: getMarketAccessTitle(country.name),
                    description: standard.description,
                });
            }
        }

        return results;
    }, [selectedKeys, countryLookup]);

    if (entries.length === 0) {
        return <div style={{ flex: 509 }} />;
    }

    return (
        <div className="flex flex-column gap-3" style={{ flex: 509 }}>
            {entries.map((entry, index) => (
                <MarketAccessEntry
                    key={`${entry.title}-${index}`}
                    title={entry.title}
                    description={entry.description}
                />
            ))}
        </div>
    );
}

export function ProjectFormRequirements(): ReactNode {
    const { control, clearErrors } = useFormContext<ProjectFormAttributes>();
    const {
        data: countriesData,
        loading: countriesLoading,
        error: countriesError,
    } = useQuery(GET_COUNTRIES_TREE);

    const countries = useMemo(
        () => flatMapEdges(countriesData?.countries) as CountryTreeNode[],
        [countriesData],
    );
    const countryTreeData = useMemo(
        () => mapCountriesToTreeNodes(countries),
        [countries],
    );
    const countryLookup = useMemo(
        () => buildCountryLookup(countries),
        [countries],
    );

    const deliveryCountryKeys = useWatch({
        name: 'deliveryCountryKeys',
        control,
    });

    return (
        <div className="flex flex-column gap-4">
            <InfoBox
                severity="info"
                items={['Please add market access requirement if necessary.']}
            />
            {countriesError ? (
                <InfoBox
                    severity="error"
                    items={[
                        'Countries list could not be loaded. Please try again later.',
                    ]}
                />
            ) : null}
            <Section title="Market access requirement">
                <div className="flex flex-column gap-3 p-4 bg-n10 border-round-sm">
                    <ModalDivided>
                        <DeliveryCountrySelector
                            clearErrors={clearErrors}
                            options={countryTreeData}
                            loading={countriesLoading}
                        />
                        <MarketAccessPanel
                            deliveryCountryKeys={deliveryCountryKeys}
                            countryLookup={countryLookup}
                        />
                    </ModalDivided>
                </div>
            </Section>
        </div>
    );
}
